import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { useRecordContext, useGetList, useGetIdentity, useGetMany } from 'react-admin';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Funeral, CustomerEvent, CustomerEventType, Partner } from '../types';
import { FeedSuggestedReply, FeedWorkItem } from './FeedSuggestedReply';
import { FeedIngestCard, IngestWorkItem } from './FeedIngestCard';
import { acceptWorkItem, supersedeWorkItem } from './workItemsApi';
import {
  SmsBubble,
  PhoneEventBubble,
  EmailEventBubble,
  SystemEventBubble,
  NoteBubble,
  DateDivider,
} from './components/EventBubbles';
import { MessageInputBar } from './components/MessageInputBar';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import { CasePartnerChip } from './components/PartnerRecipientChip';
import { usePartnerDocsStatus } from '../components/dispatch/usePartnerDocsStatus';
import { promptable, PartnerRole } from '../components/dispatch/partnerDocRequirements';
import { supabase } from '../lib/supabaseClient';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { EmailThreadCard, EmailThreadModal } from './components/EmailThread';
import {
  EmailHeaders,
  EmailThread,
  StoredAttachment,
  communicationEmailIds,
  groupEmailThreads,
  mergeStoredAttachments,
} from './emailThreads';
import { apiFetch } from '../lib/apiFetch';
import { useCaseEmailAddress } from './CopyCaseEmailButton';
import { useEmailBodies } from './useEmailBodies';

/** Emails as one card per thread + a thread modal, instead of inline-expanding bubbles (T-068). */
export const EMAIL_THREAD_MODAL_FLAG = 'feed_email_thread_modal_enabled';

function isSameDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}

/** Normalize phone to digits-only for comparison */
function normalizePhone(phone?: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

/** Event types that represent client-facing communication */
const CLIENT_EVENT_TYPES = new Set<CustomerEventType>([
  CustomerEventType.TEXT_MESSAGE,
  CustomerEventType.CUSTOMER_MESSAGE,
  CustomerEventType.PHONE_CALL,
  CustomerEventType.EMAIL,
]);

interface FuneralShowFeedProps {
  coloredClients: { id: number; name: string; color: string; phone_number: string; email?: string | null }[];
  activeClientIds: Set<number>;
  clientByRecipientId: Map<number, number>;
  clientByContactDetail: Map<string, number>;
  clientColorMap: Map<number, string>;
  showSystemEvents: boolean;
  /** Partner chips in the composer's recipient row. The inbox turns them off. */
  showPartners?: boolean;
  /** Opens the thread holding this email once it has loaded. */
  openEmailId?: string;
}

export function FuneralShowFeed({
  coloredClients,
  activeClientIds,
  clientByRecipientId,
  clientByContactDetail,
  clientColorMap,
  showSystemEvents,
  showPartners = true,
  openEmailId,
}: FuneralShowFeedProps) {
  const record = useRecordContext<Funeral>();
  const [optimisticMessages, setOptimisticMessages] = useState<CustomerEvent[]>([]);

  // ── Fetch events ──────────────────────────────────────────────────────────
  const { data: fetchedEvents } = useGetList(
    'funeral_feed',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'timestamp', order: 'ASC' },
      filter: { funeral_id: record?.id },
    },
    { enabled: !!record?.id },
  );

  // ── Suggested reply (the agent's open SMS work item for this case) ──────────
  const { identity } = useGetIdentity();
  const { data: workItems } = useGetList(
    'agent_tasks',
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'created_at', order: 'DESC' },
      filter: { case_id: record?.id, status: 'pending' },
    },
    { enabled: !!record?.id },
  );
  // Items resolved this session (used→sent or dismissed) — hide the card
  // immediately, before the list naturally refetches.
  const [resolvedWorkItemIds, setResolvedWorkItemIds] = useState<Set<string>>(
    new Set(),
  );
  // The work item whose draft is loaded in the composer — closed + reviewed once
  // the reviewer actually sends the SMS.
  const [usedWorkItemId, setUsedWorkItemId] = useState<string | null>(null);
  const suggestedReply = useMemo<FeedWorkItem | null>(() => {
    const wi = (workItems || []).find(
      (w: any) =>
        w?.metadata?.drafted_action?.channel === 'sms' &&
        w?.metadata?.drafted_action?.body &&
        !resolvedWorkItemIds.has(String(w.id)),
    );
    return (wi as FeedWorkItem) ?? null;
  }, [workItems, resolvedWorkItemIds]);

  // Ingest suggestions (parsed field writes) stack ABOVE the reply card. A single
  // inbound can produce both an ingest item and a reply ack — show both, not one
  // queued behind the other.
  const ingestItems = useMemo(
    () =>
      (workItems || []).filter(
        (w: any) =>
          w?.metadata?.kind === 'ingest' && !resolvedWorkItemIds.has(String(w.id)),
      ) as IngestWorkItem[],
    [workItems, resolvedWorkItemIds],
  );

  // Track the fetch count to detect when server data refreshes
  const prevFetchedRef = useRef(fetchedEvents);

  // Clear optimistic messages when server data actually changes (new events arrived)
  useEffect(() => {
    if (fetchedEvents && fetchedEvents !== prevFetchedRef.current && optimisticMessages.length > 0) {
      // Server data changed — check if any new outbound SMS arrived that matches our optimistic messages
      const prevLen = prevFetchedRef.current?.length ?? 0;
      if (fetchedEvents.length > prevLen) {
        setOptimisticMessages([]);
      }
    }
    prevFetchedRef.current = fetchedEvents;
  }, [fetchedEvents, optimisticMessages]);

  // Merge fetched events with optimistic messages
  const feedEvents = useEmailBodies(fetchedEvents as CustomerEvent[] | undefined);

  const events = useMemo(() => {
    if (!feedEvents) return optimisticMessages.length > 0 ? optimisticMessages : undefined;
    if (optimisticMessages.length === 0) return feedEvents;
    return [...feedEvents, ...optimisticMessages];
  }, [feedEvents, optimisticMessages]);

  const hasMultipleClients = coloredClients.length >= 2;

  // ── Partner chips for the composer (plan §3.8) ──────────────────────────
  // The partners holding a role on this case: crematorium, then the drop-off
  // location (when it isn't the crematorium itself), then transport only when
  // it has an email — we almost never write to transport.
  const partnerRoles = useMemo(() => {
    const out: Array<{ partnerId: string; role: PartnerRole }> = [];
    const add = (id: unknown, role: PartnerRole) => {
      if (id && !out.some((p) => p.partnerId === String(id))) out.push({ partnerId: String(id), role });
    };
    add(record?.crematorium_partner_id, 'crematorium');
    add(record?.dropoff_partner_id, 'dropoff');
    add(record?.transport_partner_id, 'transport');
    return out;
  }, [record?.crematorium_partner_id, record?.dropoff_partner_id, record?.transport_partner_id]);
  const { data: casePartners } = useGetMany<Partner>(
    'partners',
    { ids: partnerRoles.map((p) => p.partnerId) },
    { enabled: partnerRoles.length > 0 },
  );
  const { status: partnerDocs } = usePartnerDocsStatus(record?.id, partnerRoles.length > 0);
  const partnerChips = useMemo<CasePartnerChip[]>(() => {
    const owed = partnerDocs ? promptable(partnerDocs) : [];
    return partnerRoles.flatMap(({ partnerId, role }) => {
      const partner = casePartners?.find((p) => String(p.id) === partnerId);
      if (!partner) return [];
      const email = partner.email?.trim() || null;
      if (role === 'transport' && !email) return [];
      return [{
        partnerId,
        role,
        name: partner.name,
        email,
        missingDocs: owed.filter((i) => i.partnerId === partnerId),
      }];
    });
  }, [partnerRoles, casePartners, partnerDocs]);

  // ── Email threads (flag feed_email_thread_modal_enabled) ──────────────────
  // funeral_feed has one row per email but not the thread headers, so read
  // those from communications for the feed's email rows and group client-side.
  const emailThreadsOn = useFeatureFlag(EMAIL_THREAD_MODAL_FLAG);
  const emailIds = useMemo(
    () => (emailThreadsOn ? communicationEmailIds(fetchedEvents as CustomerEvent[] | undefined) : []),
    [emailThreadsOn, fetchedEvents],
  );
  const { data: emailHeaders } = useQuery(
    ['funeral_feed_email_headers', record?.id, emailIds.join(',')],
    async () => {
      const [{ data, error }, stored] = await Promise.all([
        supabase
          .from('communications')
          .select('id, from_identifier, to_identifier, email_cc, rfc_message_id, email_in_reply_to, attachments')
          .in('id', emailIds),
        // Stored files (T-139) come from the backend: the table is staff-only.
        // A failure here only costs the download links, not the thread.
        apiFetch(`email-attachments?communication_ids=${encodeURIComponent(emailIds.join(','))}`).catch(
          (err) => {
            console.error('Email attachments failed to load:', err);
            return [];
          },
        ) as Promise<StoredAttachment[]>,
      ]);
      if (error) throw error;
      return mergeStoredAttachments((data || []) as EmailHeaders[], stored || []);
    },
    { enabled: emailIds.length > 0, staleTime: 60 * 1000 },
  );
  const emailThreads = useMemo(() => {
    if (!emailThreadsOn) return null;
    const byId = new Map((emailHeaders || []).map((h) => [String(h.id), h]));
    return groupEmailThreads(events, byId);
  }, [emailThreadsOn, events, emailHeaders]);
  // Each thread renders once, at its newest message.
  const threadByLatestEventId = useMemo(() => {
    const map = new Map<string, EmailThread>();
    for (const t of emailThreads || []) map.set(String(t.latest.event.id), t);
    return map;
  }, [emailThreads]);

  // The open thread lives in the URL (?email=<threadId>) so it can be linked to.
  const [searchParams, setSearchParams] = useSearchParams();
  const openThreadId = emailThreadsOn ? searchParams.get('email') : null;
  const openThread = useMemo(
    () => (openThreadId ? (emailThreads || []).find((t) => t.id === openThreadId) ?? null : null),
    [openThreadId, emailThreads],
  );
  const setOpenThreadId = useCallback(
    (id: string | null) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('email', id);
          else next.delete('email');
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // Opened from an email row in the inbox. Once per email: closing the viewer
  // must not reopen it on the next refetch.
  const openedEmailRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openEmailId || openedEmailRef.current === openEmailId) return;
    const thread = (emailThreads || []).find((t) =>
      t.messages.some((m) => String(m.event.id) === openEmailId),
    );
    if (!thread) return;
    openedEmailRef.current = openEmailId;
    setOpenThreadId(thread.id);
  }, [openEmailId, emailThreads, setOpenThreadId]);

  const caseAddress = useCaseEmailAddress(emailThreadsOn ? record?.id : undefined);

  // ── Match event → client ──────────────────────────────────────────────────
  const getEventClientId = useCallback(
    (event: CustomerEvent): number | null => {
      if (event.recipient_id && (!event.recipient_type || event.recipient_type === 'customer')) {
        if (clientByRecipientId.has(event.recipient_id)) {
          return event.recipient_id;
        }
      }
      if (event.contact_detail) {
        const detail = event.contact_detail.trim();
        const emailMatch = clientByContactDetail.get(detail.toLowerCase());
        if (emailMatch !== undefined) return emailMatch;
        const phoneMatch = clientByContactDetail.get(normalizePhone(detail));
        if (phoneMatch !== undefined) return phoneMatch;
      }
      return null;
    },
    [clientByRecipientId, clientByContactDetail],
  );

  // ── Client name map ────────────────────────────────────────────────────────
  const clientNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of coloredClients) {
      map.set(c.id, c.name);
    }
    return map;
  }, [coloredClients]);

  // ── Render helper ─────────────────────────────────────────────────────────
  const renderEventBubble = useCallback(
    (event: CustomerEvent, matchedClientId: number | null) => {
      const clientColor = hasMultipleClients && matchedClientId ? clientColorMap.get(matchedClientId) : undefined;
      const clientName = hasMultipleClients && matchedClientId ? clientNameMap.get(matchedClientId) : undefined;

      switch (event.type) {
        case CustomerEventType.TEXT_MESSAGE:
        case CustomerEventType.CUSTOMER_MESSAGE:
          return <SmsBubble key={event.id} event={event} clientColor={clientColor} clientName={clientName} />;
        case CustomerEventType.PHONE_CALL:
          return <PhoneEventBubble key={event.id} event={event} clientColor={clientColor} clientName={clientName} />;
        case CustomerEventType.EMAIL: {
          const thread = threadByLatestEventId.get(String(event.id));
          if (thread) {
            return (
              <EmailThreadCard
                key={event.id}
                thread={thread}
                clients={coloredClients}
                partners={partnerChips}
                clientColor={clientColor}
                clientName={clientName}
                onOpen={(t) => setOpenThreadId(t.id)}
              />
            );
          }
          return <EmailEventBubble key={event.id} event={event} clientColor={clientColor} clientName={clientName} />;
        }
        case CustomerEventType.INTERNAL_NOTE:
          return <NoteBubble key={event.id} event={event} />;
        default:
          return <SystemEventBubble key={event.id} event={event} />;
      }
    },
    [hasMultipleClients, clientColorMap, clientNameMap, threadByLatestEventId, coloredClients, partnerChips, setOpenThreadId],
  );

  // ── Default recipient: last client who sent an inbound message ──────────
  const defaultRecipientId = useMemo(() => {
    if (!events || coloredClients.length < 2) return coloredClients[0]?.id ?? null;
    // Walk backwards to find the most recent inbound (customer_message) event matched to a client
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (ev.type === CustomerEventType.CUSTOMER_MESSAGE) {
        const cid = getEventClientId(ev);
        if (cid !== null) return cid;
      }
    }
    // Fallback: last client-facing event of any kind
    for (let i = events.length - 1; i >= 0; i--) {
      const cid = getEventClientId(events[i]);
      if (cid !== null) return cid;
    }
    return coloredClients[0]?.id ?? null;
  }, [events, coloredClients, getEventClientId]);

  // ── Most recent inbound sender ──────────────────────────────────────────
  // The client who sent the latest received (customer_message) SMS, used to
  // warn when the user is about to reply to a *different* contact. No fallback:
  // null when the latest inbound can't be matched to a known client (so we
  // only warn when we can positively name a different recent sender).
  const lastInboundClientId = useMemo(() => {
    if (!events) return null;
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].type === CustomerEventType.CUSTOMER_MESSAGE) {
        return getEventClientId(events[i]);
      }
    }
    return null;
  }, [events, getEventClientId]);

  // Open the case at its newest message, and keep it pinned there while the
  // thread settles. The previous version walked up for the first ancestor whose
  // scrollHeight exceeded its clientHeight, which is also true of the
  // `overflow: hidden` wrappers this feed sits inside when it is mounted in the
  // docked inbox — it scrolled one of those instead, to no visible effect, and
  // the once-per-funeral guard meant it never tried again.
  //
  // Wait for this case's events before pinning. Keyed on the id alone, the
  // settle window ran against the empty "No events yet" placeholder while the
  // fetch was in flight, stopped after 400ms, and the events then rendered
  // with the feed parked at the top — on every case opened or switched to.
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { endRef: feedEndRef, scrollToBottom } = useScrollToBottom(
    record?.id,
    fetchedEvents !== undefined,
  );

  const handleMessageSent = useCallback((sentMessage: CustomerEvent) => {
    setOptimisticMessages((prev) => [...prev, sentMessage]);
    setTimeout(scrollToBottom, 50);
    // If this send came from a "Suggested reply" draft, close the work item and
    // record the review (edited vs approved, from the actually-sent body).
    if (usedWorkItemId && sentMessage.type === CustomerEventType.TEXT_MESSAGE) {
      const wid = usedWorkItemId;
      setUsedWorkItemId(null);
      setResolvedWorkItemIds((s) => new Set(s).add(wid));
      const reviewer = identity
        ? { id: String(identity.id), name: identity.fullName }
        : undefined;
      void acceptWorkItem(wid, sentMessage.message ?? '', reviewer).catch((e) =>
        console.warn('acceptWorkItem failed', e),
      );
    } else if (
      suggestedReply?.task_type === 'inbound_reply' &&
      sentMessage.type === CustomerEventType.TEXT_MESSAGE
    ) {
      // A from-scratch reply (didn't click "Use") still answers the family, so
      // close the reply suggestion now instead of waiting for the hourly reconcile
      // — and log it as 'superseded' (used another way), not silently.
      const wid = suggestedReply.id;
      setResolvedWorkItemIds((s) => new Set(s).add(wid));
      const reviewer = identity
        ? { id: String(identity.id), name: identity.fullName }
        : undefined;
      void supersedeWorkItem(wid, sentMessage.message ?? '', reviewer).catch((e) =>
        console.warn('supersedeWorkItem failed', e),
      );
    }
  }, [scrollToBottom, usedWorkItemId, identity, suggestedReply]);

  // ── Build filtered event elements ─────────────────────────────────────────
  const eventElements = useMemo(() => {
    if (!events || events.length === 0) return [];

    const elements: React.ReactNode[] = [];
    let lastDate = '';

    for (const event of events) {
      const matchedClientId = getEventClientId(event);

      // Hide client events when that client is toggled off
      if (
        matchedClientId !== null &&
        CLIENT_EVENT_TYPES.has(event.type) &&
        !activeClientIds.has(matchedClientId)
      ) {
        continue;
      }

      // Hide system events when toggled off
      if (!showSystemEvents && !CLIENT_EVENT_TYPES.has(event.type)) {
        continue;
      }

      // Thread mode: older messages live in their thread's card.
      if (
        emailThreads &&
        event.type === CustomerEventType.EMAIL &&
        !threadByLatestEventId.has(String(event.id))
      ) {
        continue;
      }

      const eventDate = event.timestamp;
      if (!lastDate || !isSameDay(lastDate, eventDate)) {
        elements.push(<DateDivider key={`date-${eventDate}`} date={eventDate} />);
        lastDate = eventDate;
      }
      elements.push(renderEventBubble(event, matchedClientId));
    }

    return elements;
  }, [events, activeClientIds, showSystemEvents, getEventClientId, renderEventBubble, emailThreads, threadByLatestEventId]);

  if (!record) return null;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-x-hidden">
      {/* Scrollable feed */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 min-h-0">
        <div className="px-3 sm:px-4 py-2 space-y-1 overflow-hidden">
          {eventElements.length > 0 ? (
            eventElements
          ) : (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              No events yet for this case.
            </div>
          )}
          <div ref={feedEndRef} />
        </div>
      </ScrollArea>

      {/* Agent suggestions, stacked above the composer. Priority: ingest > reply. */}
      {ingestItems.map((wi) => (
        <FeedIngestCard
          key={wi.id}
          workItem={wi}
          onResolved={(id) =>
            setResolvedWorkItemIds((s) => new Set(s).add(id))
          }
        />
      ))}
      {suggestedReply && (
        <FeedSuggestedReply
          workItem={suggestedReply}
          onUse={setUsedWorkItemId}
          onResolved={(id) =>
            setResolvedWorkItemIds((s) => new Set(s).add(id))
          }
        />
      )}

      {emailThreadsOn && (
        <EmailThreadModal
          thread={openThread}
          onClose={() => setOpenThreadId(null)}
          funeralId={Number(record.id)}
          deceasedClientId={record.deceased_client_id}
          clients={coloredClients}
          partners={partnerChips}
          caseAddress={caseAddress}
        />
      )}

      {/* Message input bar */}
      <MessageInputBar
        clients={coloredClients}
        partners={partnerChips}
        showPartners={showPartners}
        defaultRecipientId={defaultRecipientId}
        lastInboundClientId={lastInboundClientId}
        onMessageSent={handleMessageSent}
      />
    </div>
  );
}
